import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';

import { MobileContextService } from '@core/context/services/mobile-context.service';
import { environment } from '@environments/environment';
import { ContextoPWA } from '@shared/types';

import { PlanoEnsinoService } from '../services/plano-ensino.service';

interface ComponenteItem {
  id: number;
  descricao?: string;
  nome?: string;
}

@Component({
  selector: 'app-plano-ensino',
  templateUrl: './plano-ensino.component.html'
})
export class PlanoEnsinoComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private etapaIdPorComponenteId: Record<number, number> = {};

  contexto: ContextoPWA | null = null;
  semContexto = false;
  carregando = false;
  dropdownAberto = false;

  componentes: ComponenteItem[] = [];
  itemAtual: ComponenteItem | null = null;
  campoAdicional: any = null;

  get indiceAtual(): number {
    if (!this.itemAtual) return -1;
    return this.componentes.findIndex((c) => c.id === this.itemAtual!.id);
  }

  get podePrev(): boolean {
    return this.indiceAtual > 0;
  }

  get podeNext(): boolean {
    return this.indiceAtual < this.componentes.length - 1;
  }

  get labelAtual(): string {
    return this.itemAtual?.descricao || this.itemAtual?.nome || '';
  }

  constructor(
    private planoEnsinoService: PlanoEnsinoService,
    private mobileContextService: MobileContextService
  ) {}

  ngOnInit(): void {
    this.mobileContextService.contexto$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((a, b) => this.gerarChaveContexto(a) === this.gerarChaveContexto(b))
      )
      .subscribe((contexto) => this.aoMudarContexto(contexto));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  prev(): void {
    const idx = this.indiceAtual;
    if (idx > 0) {
      this.selecionarComponente(this.componentes[idx - 1]);
    }
  }

  next(): void {
    const idx = this.indiceAtual;
    if (idx < this.componentes.length - 1) {
      this.selecionarComponente(this.componentes[idx + 1]);
    }
  }

  selecionarComponente(item: ComponenteItem): void {
    this.itemAtual = item;
    this.carregarCampoAdicionalDoComponente();
  }

  private aoMudarContexto(contexto: ContextoPWA | null): void {
    this.contexto = contexto;
    this.etapaIdPorComponenteId = {};
    this.campoAdicional = null;
    this.componentes = [];
    this.itemAtual = null;

    const turmaId = contexto?.turma?.id;

    if (!turmaId) {
      this.semContexto = true;
      return;
    }

    this.semContexto = false;
    this.carregando = true;

    this.planoEnsinoService
      .getFilhosDaTurma(turmaId)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.carregando = false)))
      .subscribe({
        next: (filhos: any[]) => {
          this.componentes = this.construirComponentesDosFilhos(filhos, contexto);
          if (this.componentes.length > 0) {
            this.selecionarComponente(this.componentes[0]);
          }
        },
        error: () => undefined
      });
  }

  private construirComponentesDosFilhos(
    filhos: any[],
    contexto: ContextoPWA | null
  ): ComponenteItem[] {
    const idsDoContexto = new Set(
      (contexto?.componentes || []).map((c) => c.id)
    );

    const componentesEncontrados: ComponenteItem[] = [];

    (filhos || []).forEach((filho: any) => {
      const componenteId: number = filho?.id;
      const etapaMatrizDisciplinaId: number = filho?.etapaMatrizDisciplina?.id;

      if (!componenteId || !etapaMatrizDisciplinaId) {
        return;
      }

      this.etapaIdPorComponenteId[componenteId] = etapaMatrizDisciplinaId;

      const pertenceAoContexto = !idsDoContexto.size || idsDoContexto.has(componenteId);
      if (!pertenceAoContexto) {
        return;
      }

      const etapa = filho.etapaMatrizDisciplina;
      const descricao =
        etapa?.disciplina?.descricao ||
        etapa?.campoExperiencia?.descricao ||
        etapa?.eixoTematico?.descricao ||
        '';

      componentesEncontrados.push({ id: componenteId, descricao });
    });

    return componentesEncontrados;
  }


  private carregarCampoAdicionalDoComponente(): void {
    if (!this.itemAtual) {
      this.campoAdicional = null;
      return;
    }

    const etapaMatrizDisciplinaId = this.etapaIdPorComponenteId[this.itemAtual.id];
    if (!etapaMatrizDisciplinaId) {
      this.campoAdicional = null;
      return;
    }

    this.campoAdicional = null;
    this.carregando = true;

    this.planoEnsinoService
      .getCamposAdicionais(etapaMatrizDisciplinaId)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.carregando = false)))
      .subscribe({
        next: (resposta: any) => {
          this.campoAdicional = this.extrairConfiguracao(resposta);
        },
        error: () => {
          this.campoAdicional = null;
        }
      });
  }

  private extrairConfiguracao(resposta: any): any {
    const configs: any[] = Array.isArray(resposta) ? resposta : [resposta];
    const identificador = environment.identificadorCamposAdicionaisPlanoEnsino;

    const configuracoesFiltradas = identificador
      ? configs.filter((c) => c?.identificadorConfiguracao === identificador)
      : configs;

    configuracoesFiltradas.forEach((config) => {
      (config?.agrupadores || []).forEach((agrupador: any) => {
        this.normalizarCamposDoAgrupador(agrupador);
      });
    });

    return configuracoesFiltradas.length ? configuracoesFiltradas[0] : null;
  }

  private normalizarCamposDoAgrupador(agrupador: any): void {
    const campos: any[] = agrupador?.campos || [];

    campos.sort((a: any, b: any) => {
      const linhaA = a?.estilos?.height ?? 0;
      const linhaB = b?.estilos?.height ?? 0;
      if (linhaA !== linhaB) return linhaA - linhaB;
      return (a?.estilos?.width ?? 0) - (b?.estilos?.width ?? 0);
    });

    campos.forEach((campo: any) => this.normalizarCampo(campo));
  }

  private normalizarCampo(campo: any): void {
    if (!campo.configuracoes) {
      campo.configuracoes = {};
    }
    campo.configuracoes.readOnly = true;

    if (campo.formato === 'DATE') {
      campo.valor = this.formatarValorData(campo.valor, campo.configuracoes.date);
    }
  }

  private formatarValorData(valor: any, pattern = 'dd/mm/yy'): any {
    if (!valor || typeof valor !== 'string') {
      return valor;
    }

    const isoMatch = valor.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!isoMatch) {
      return valor;
    }

    const [, ano, mes, dia] = isoMatch;
    return pattern
      .replace('dd', dia)
      .replace('mm', mes)
      .replace('yy', ano);
  }

  private gerarChaveContexto(contexto: ContextoPWA | null): string {
    // Inclui os IDs dos componentes na chave pra troca de componente (sem trocar turma) disparar recarregamento.
    const componenteIds = (contexto?.componentes || [])
      .map((c) => c.id)
      .sort((a, b) => a - b)
      .join(',');
    return JSON.stringify({ turmaId: contexto?.turma?.id, componenteIds });
  }
}
